import type { GrammarRule } from './grammar'

/**
 * Spanish grammar, from A1 to C2.
 *
 * The first hard things in Spanish are distinctions English doesn’t draw: ser
 * or estar, tú or usted, preterite or imperfect, por or para, indicative or
 * subjunctive. Each gets a rule of its own, with examples chosen in pairs so
 * the contrast is visible rather than described. The subjunctive grows across
 * four levels, because that is how learners actually meet it: after querer
 * que first, then in si clauses, then as the marker of an imagined thing,
 * and finally as a choice that changes meaning.
 *
 * Every question opens with ¿ and every exclamation with ¡; grammar.test.ts
 * enforces it.
 */
export const GRAMMAR_ES: GrammarRule[] = [
  // ------------------------------------------------------------------ A1
  {
    id: 'es-gender',
    level: 1,
    topic: 'nouns',
    title: 'Every noun is masculine or feminine',
    rule: 'Spanish nouns have a gender, shown by the article: el or un for masculine, la or una for feminine. Most nouns ending in -o are masculine and most in -a feminine, with a few famous exceptions.',
    pattern: [{ word: ['el', 'la', 'un', 'una'] }, 'Noun'],
    examples: [
      { text: '[El](el) [libro](Noun) está en la mesa.', english: 'The book is on the table.' },
      { text: '[La](la) [casa](Noun) es muy bonita.', english: 'The house is very pretty.' },
      { text: 'Tengo [un](un) [problema](Noun).', english: 'I have a problem.' },
      { text: 'Tengo [la](la) [mano](Noun) fría.', english: 'My hand is cold.' },
    ],
    note: 'Watch for the exceptions: el problema, el día and el mapa are masculine; la mano and la foto are feminine.',
  },
  {
    id: 'es-ser-estar',
    level: 1,
    topic: 'verbs',
    title: 'ser for what something is, estar for how or where it is',
    rule: 'Both mean “to be”. ser is for identity, origin, profession and lasting traits. estar is for location, and for states and conditions that can change.',
    pattern: ['Form of ser', 'Form of estar'],
    shape: 'contrast',
    examples: [
      { text: '[Soy](Form of ser) de Chicago.', english: 'I’m from Chicago.' },
      { text: '[Estoy](Form of estar) cansado.', english: 'I’m tired.' },
      { text: 'Mi hermana [es](Form of ser) médica.', english: 'My sister is a doctor.' },
      { text: 'Madrid [está](Form of estar) en el centro de España.', english: 'Madrid is in the center of Spain.' },
    ],
    pitfall: {
      wrong: 'Soy cansado.',
      right: 'Estoy cansado.',
      why: 'Being tired is a state you are in, so it takes estar.',
    },
    note: 'One surprise: events take ser, even for where they happen — la fiesta es en mi casa.',
  },
  {
    id: 'es-dropped-pronouns',
    level: 1,
    topic: 'verbs',
    title: 'The verb ending says who, so the pronoun usually goes',
    rule: 'Spanish verb endings show the subject, so yo, tú and the rest are usually left out. Use them only for contrast or emphasis.',
    pattern: ['Verb ending'],
    examples: [
      { text: '[Habl**o**](Verb ending) inglés y español.', english: 'I speak English and Spanish.' },
      { text: '¿[Vien**es**](Verb ending) mañana?', english: 'Are you coming tomorrow?' },
      { text: '[Viv**imos**](Verb ending) en Texas.', english: 'We live in Texas.' },
    ],
    note: 'Add the pronoun to contrast two people: Yo pago, tú no.',
  },
  {
    id: 'es-negation',
    level: 1,
    topic: 'negation',
    title: 'no before the verb — and double negatives are required',
    rule: 'Put no directly before the verb. When nada, nadie or nunca comes after the verb, no still goes in front: Spanish needs the double negative.',
    pattern: [{ word: 'no' }, 'Verb', 'Negative word'],
    examples: [
      { text: '[No](no) [hablo](Verb) francés.', english: 'I don’t speak French.' },
      { text: '[No](no) [veo](Verb) a [nadie](Negative word).', english: 'I don’t see anyone.' },
      { text: '[No](no) [quiero](Verb) [nada](Negative word).', english: 'I don’t want anything.' },
    ],
    pitfall: {
      wrong: 'Quiero nada.',
      right: 'No quiero nada.',
      why: 'With nada after the verb, no must come before it.',
    },
    note: 'If the negative word comes first, drop no: Nadie vino.',
  },
  {
    id: 'es-adjectives',
    level: 1,
    topic: 'nouns',
    title: 'Adjectives follow the noun and agree with it',
    rule: 'Spanish adjectives usually come after the noun, and they match it in gender and number.',
    pattern: ['Noun', 'Adjective'],
    examples: [
      { text: 'Tengo un [coche](Noun) [roj**o**](Adjective).', english: 'I have a red car.' },
      { text: 'Vivimos en una [casa](Noun) [blanc**a**](Adjective).', english: 'We live in a white house.' },
      { text: 'Me gustan las [ciudades](Noun) [pequeñ**as**](Adjective).', english: 'I like small cities.' },
    ],
    pitfall: {
      wrong: 'Tengo un rojo coche.',
      right: 'Tengo un coche rojo.',
      why: 'Colors always follow the noun.',
    },
    note: 'A few adjectives change meaning with position: un gran hombre is a great man, un hombre grande a big one.',
  },
  {
    id: 'es-gustar',
    level: 1,
    topic: 'verbs',
    title: 'gustar: the thing you like is the subject',
    rule: 'Me gusta means “it pleases me”. The thing liked is the subject, so the verb agrees with it — gusta for one thing, gustan for several — and the person is an indirect object pronoun.',
    pattern: ['Who likes it', 'Verb', 'What is liked'],
    examples: [
      { text: '[Me](Who likes it) [gusta](Verb) [el café](What is liked).', english: 'I like coffee.' },
      { text: '[Me](Who likes it) [gustan](Verb) [los perros](What is liked).', english: 'I like dogs.' },
      { text: '¿[Te](Who likes it) [gusta](Verb) [bailar](What is liked)?', english: 'Do you like dancing?' },
      {
        text: 'A mi hermana [le](Who likes it) [encantan](Verb) [las películas de terror](What is liked).',
        english: 'My sister loves horror movies.',
      },
    ],
    pitfall: {
      wrong: 'Yo gusto el café.',
      right: 'Me gusta el café.',
      why: 'The coffee does the pleasing, so it is the subject and the verb agrees with it.',
    },
  },
  {
    id: 'es-tu-usted',
    level: 1,
    topic: 'register',
    title: 'tú for friends, usted for respect',
    rule: 'tú is informal. usted is polite, and takes the same verb form as él and ella. In much of Latin America, ustedes is the only plural “you”.',
    pattern: [{ word: 'tú' }, { word: 'usted' }],
    shape: 'contrast',
    examples: [
      { text: '¿Y [tú](tú), de dónde eres?', english: 'And you, where are you from?' },
      { text: '¿Y [usted](usted), de dónde es?', english: 'And you, where are you from? (formal)' },
      { text: '¿Pued**es** ayudarme?', english: 'Can you help me?' },
      { text: '¿Puede [usted](usted) ayudarme?', english: 'Could you help me?' },
    ],
    note: 'usted takes the él and ella form: usted es, usted puede. Spain also has a separate informal plural, vosotros.',
  },

  // ------------------------------------------------------------------ A2
  {
    id: 'es-ir-a',
    level: 2,
    topic: 'verbs',
    title: 'ir a + infinitive: what’s going to happen',
    rule: 'A present form of ir, then a, then an infinitive: the everyday way to talk about plans and the near future.',
    pattern: ['Form of ir', { word: 'a' }, 'Infinitive'],
    examples: [
      { text: '[Voy](Form of ir) [a](a) [llamar](Infinitive) a mi madre.', english: 'I’m going to call my mother.' },
      {
        text: '¿Qué [vas](Form of ir) [a](a) [hacer](Infinitive) este fin de semana?',
        english: 'What are you going to do this weekend?',
      },
      { text: '[Va](Form of ir) [a](a) [llover](Infinitive).', english: 'It’s going to rain.' },
    ],
  },
  {
    id: 'es-personal-a',
    level: 2,
    topic: 'nouns',
    title: 'The personal a before a person',
    rule: 'When the direct object is a specific person, or a pet, Spanish puts a in front of it. It isn’t translated.',
    pattern: ['Verb', { word: 'a' }, 'Person'],
    examples: [
      { text: '[Veo](Verb) [a](a) [mi madre](Person) los domingos.', english: 'I see my mother on Sundays.' },
      { text: '¿[Conoces](Verb) [a](a) [Carlos](Person)?', english: 'Do you know Carlos?' },
      { text: '[Busco](Verb) un taxi.', english: 'I’m looking for a taxi.' },
    ],
    pitfall: {
      wrong: 'Llamo mi hermano.',
      right: 'Llamo a mi hermano.',
      why: 'Your brother is a person, so he takes the personal a.',
    },
    note: 'A taxi isn’t a person, so no a. And a + el merges into al: Veo al médico mañana.',
  },
  {
    id: 'es-object-pronouns',
    level: 2,
    topic: 'verbs',
    title: 'Object pronouns: before the verb, or on the end',
    rule: 'lo, la, le, me, te and the rest go before a conjugated verb. They attach to the end of an infinitive, a gerund, or a positive command.',
    pattern: ['Pronoun', 'Verb'],
    shape: 'contrast',
    examples: [
      { text: '[Lo](Pronoun) [veo](Verb) mañana.', english: 'I’ll see him tomorrow.' },
      { text: 'Quiero [ver**lo**](Pronoun) mañana.', english: 'I want to see him tomorrow.' },
      { text: '¿[Me](Pronoun) [ayudas](Verb)?', english: 'Can you help me?' },
      { text: '[Dí**melo**](Pronoun).', english: 'Tell me.' },
    ],
    pitfall: {
      wrong: 'Veo lo mañana.',
      right: 'Lo veo mañana.',
      why: 'With a conjugated verb, the pronoun goes in front.',
    },
  },
  {
    id: 'es-progressive',
    level: 2,
    topic: 'verbs',
    title: 'estar + gerund: happening right now',
    rule: 'A form of estar plus the gerund (-ando, -iendo) describes something in progress at this moment. Unlike English, Spanish doesn’t use it for plans.',
    pattern: ['Form of estar', 'Gerund'],
    examples: [
      { text: 'Ahora [estoy](Form of estar) [trabaj**ando**](Gerund).', english: 'I’m working right now.' },
      { text: '¿Qué [estás](Form of estar) [hac**iendo**](Gerund)?', english: 'What are you doing?' },
      { text: '[Están](Form of estar) [com**iendo**](Gerund) en el jardín.', english: 'They’re eating in the garden.' },
    ],
    pitfall: {
      wrong: 'Estoy viajando a México mañana.',
      right: 'Viajo a México mañana.',
      why: 'For a plan, Spanish uses the plain present — or ir a — not estar plus the gerund.',
    },
  },

  // ------------------------------------------------------------------ B1
  {
    id: 'es-preterite-imperfect',
    level: 3,
    topic: 'verbs',
    title: 'Preterite for events, imperfect for the background',
    rule: 'The preterite reports completed events: what happened. The imperfect describes the scene, ongoing states and habits: how things were.',
    pattern: ['Imperfect', 'Preterite'],
    shape: 'contrast',
    examples: [
      { text: 'De niño [comía](Imperfect) mucha fruta.', english: 'As a kid I ate a lot of fruit.' },
      { text: 'Ayer [comí](Preterite) paella.', english: 'I had paella yesterday.' },
      { text: '[Leía](Imperfect) cuando [sonó](Preterite) el teléfono.', english: 'I was reading when the phone rang.' },
    ],
    note: 'If English could say “used to” or “was …ing”, Spanish usually wants the imperfect.',
  },
  {
    id: 'es-por-para',
    level: 3,
    topic: 'nouns',
    title: 'por and para',
    rule: 'para points toward a goal: a purpose, a recipient, a destination, a deadline. por covers the cause, the exchange, the route and the duration.',
    pattern: [{ word: 'para' }, { word: 'por' }],
    shape: 'contrast',
    examples: [
      { text: 'Este regalo es [para](para) ti.', english: 'This present is for you.' },
      { text: 'Gracias [por](por) la ayuda.', english: 'Thanks for the help.' },
      { text: 'Estudio [para](para) ser médico.', english: 'I’m studying to become a doctor.' },
      { text: 'Pasamos [por](por) el parque.', english: 'We went through the park.' },
    ],
    pitfall: {
      wrong: 'Gracias para todo.',
      right: 'Gracias por todo.',
      why: 'Thanks are given in exchange for something, so they take por.',
    },
  },
  {
    id: 'es-subjunctive-wishes',
    level: 3,
    topic: 'verbs',
    title: 'The subjunctive after wanting, hoping and doubting',
    rule: 'When one person wants, hopes, doubts or feels something about what someone else does, the second verb goes into the subjunctive after que.',
    pattern: ['Trigger', { word: 'que' }, 'Subjunctive'],
    examples: [
      { text: '[Quiero](Trigger) [que](que) [veng**as**](Subjunctive).', english: 'I want you to come.' },
      { text: '[Espero](Trigger) [que](que) [est**és**](Subjunctive) bien.', english: 'I hope you’re well.' },
      { text: '[No creo](Trigger) [que](que) [llueva](Subjunctive).', english: 'I don’t think it will rain.' },
    ],
    pitfall: {
      wrong: 'Quiero que vienes.',
      right: 'Quiero que vengas.',
      why: 'querer que always takes the subjunctive.',
    },
    note: 'With a single subject there is no que and no subjunctive: quiero venir, “I want to come”.',
  },

  // ------------------------------------------------------------------ B2
  {
    id: 'es-si-clauses',
    level: 4,
    topic: 'verbs',
    title: 'si with the imperfect subjunctive, then the conditional',
    rule: 'For something unlikely or imaginary, the si half takes the imperfect subjunctive and the result takes the conditional.',
    pattern: [{ word: 'si' }, 'Imperfect subjunctive', 'Conditional'],
    examples: [
      {
        text: '[Si](si) [tuviera](Imperfect subjunctive) tiempo, [viajaría](Conditional) más.',
        english: 'If I had time, I’d travel more.',
      },
      {
        text: '[Si](si) [fuera](Imperfect subjunctive) tú, no lo [haría](Conditional).',
        english: 'If I were you, I wouldn’t do it.',
      },
      {
        text: '¿Qué [harías](Conditional) [si](si) [ganaras](Imperfect subjunctive) la lotería?',
        english: 'What would you do if you won the lottery?',
      },
    ],
    pitfall: {
      wrong: 'Si tendría tiempo, viajaría más.',
      right: 'Si tuviera tiempo, viajaría más.',
      why: 'The conditional never goes straight after si.',
    },
  },
  {
    id: 'es-impersonal-se',
    level: 4,
    topic: 'verbs',
    title: 'se: when nobody in particular does it',
    rule: 'se with a third-person verb makes a general statement with no particular subject — “one”, “people”, or a passive. The verb agrees with the thing: se vende, se venden.',
    pattern: [{ word: 'se' }, 'Verb'],
    examples: [
      { text: 'Aquí [se](se) [habla](Verb) español.', english: 'Spanish is spoken here.' },
      { text: '[Se](se) [venden](Verb) pisos.', english: 'Apartments for sale.' },
      { text: '¿Cómo [se](se) [dice](Verb) “window” en español?', english: 'How do you say “window” in Spanish?' },
    ],
    note: 'The verb matches what’s being sold: se vende un piso, se venden pisos.',
  },
  {
    id: 'es-unknown-antecedent',
    level: 4,
    topic: 'verbs',
    title: 'Looking for something that may not exist: the subjunctive',
    rule: 'A relative clause describing something unknown or hypothetical takes the subjunctive. If the thing definitely exists, the indicative.',
    pattern: ['Known: indicative', 'Unknown: subjunctive'],
    shape: 'contrast',
    examples: [
      {
        text: 'Busco un piso que [teng**a**](Unknown: subjunctive) terraza.',
        english: 'I’m looking for an apartment with a terrace.',
      },
      { text: 'Tengo un piso que [tien**e**](Known: indicative) terraza.', english: 'I have an apartment with a terrace.' },
      {
        text: '¿Hay alguien aquí que [habl**e**](Unknown: subjunctive) inglés?',
        english: 'Is there anyone here who speaks English?',
      },
    ],
    note: 'The grammar tells the listener whether you know the thing exists.',
  },

  // ------------------------------------------------------------------ C1
  {
    id: 'es-accidental-se',
    level: 5,
    topic: 'verbs',
    title: 'se me olvidó: things that happen to you',
    rule: 'For accidents, Spanish makes the thing the subject and you the indirect object: se me olvidaron las llaves, literally “the keys forgot themselves on me”. It takes the blame off the speaker.',
    pattern: [{ word: 'se' }, 'Who it happened to', 'Verb', 'What'],
    examples: [
      { text: '[Se](se) [me](Who it happened to) [olvidaron](Verb) [las llaves](What).', english: 'I forgot my keys.' },
      { text: '[Se](se) [nos](Who it happened to) [rompió](Verb) [el coche](What).', english: 'Our car broke down.' },
      { text: '¿[Se](se) [te](Who it happened to) [cayó](Verb) [el móvil](What)?', english: 'Did you drop your phone?' },
    ],
    note: 'The verb agrees with the thing: se me olvidó la llave, se me olvidaron las llaves.',
  },
  {
    id: 'es-past-conditional',
    level: 5,
    topic: 'verbs',
    title: 'If I had known: the past that didn’t happen',
    rule: 'For something in the past that didn’t happen, the si half takes the pluperfect subjunctive and the result takes the conditional perfect.',
    pattern: [{ word: 'si' }, 'Pluperfect subjunctive', 'Conditional perfect'],
    examples: [
      {
        text: '[Si](si) lo [hubiera sabido](Pluperfect subjunctive), [habría venido](Conditional perfect).',
        english: 'If I had known, I would have come.',
      },
      {
        text: '[Si](si) [hubiéramos salido](Pluperfect subjunctive) antes, no [habríamos perdido](Conditional perfect) el tren.',
        english: 'If we had left earlier, we wouldn’t have missed the train.',
      },
    ],
    note: 'In speech the second half often takes the subjunctive too: si lo hubiera sabido, hubiera venido.',
  },
  {
    id: 'es-lo-adjective',
    level: 5,
    topic: 'nouns',
    title: 'lo + adjective: the … thing',
    rule: 'lo in front of an adjective turns it into an abstract idea: lo importante, “the important thing”; lo bueno, “the good part”.',
    pattern: [{ word: 'lo' }, 'Adjective'],
    examples: [
      { text: '[Lo](lo) [importante](Adjective) es participar.', english: 'The important thing is to take part.' },
      { text: '[Lo](lo) [bueno](Adjective) de vivir aquí es el clima.', english: 'The good thing about living here is the weather.' },
      { text: 'No te imaginas [lo](lo) [difícil](Adjective) que fue.', english: 'You can’t imagine how hard it was.' },
    ],
    note: 'lo + adjective + que means “how”: lo difícil que fue, “how hard it was”.',
  },

  // ------------------------------------------------------------------ C2
  {
    id: 'es-aunque',
    level: 6,
    topic: 'linking',
    title: 'aunque: a fact, or a possibility',
    rule: 'aunque with the indicative states a fact: “although”. With the subjunctive it concedes something that may or may not be true: “even if”.',
    pattern: [{ word: 'aunque' }, 'Fact: indicative', 'Possibility: subjunctive'],
    shape: 'contrast',
    examples: [
      { text: '[Aunque](aunque) [llueve](Fact: indicative), salgo.', english: 'Although it’s raining, I’m going out.' },
      { text: '[Aunque](aunque) [llueva](Possibility: subjunctive), saldré.', english: 'Even if it rains, I’ll go out.' },
      { text: '[Aunque](aunque) [es](Fact: indicative) caro, lo compro.', english: 'Although it’s expensive, I’m buying it.' },
      { text: '[Aunque](aunque) [sea](Possibility: subjunctive) caro, lo compraré.', english: 'Even if it’s expensive, I’ll buy it.' },
    ],
    note: 'The subjunctive can also brush aside a fact the other person raised: Aunque sea caro, lo quiero — expensive or not, I want it.',
  },
  {
    id: 'es-por-mucho-que',
    level: 6,
    topic: 'linking',
    title: 'por mucho que: no matter how much',
    rule: 'por mucho que and por más que concede that effort makes no difference. Looking ahead, or speaking generally, they take the subjunctive.',
    pattern: [{ word: ['por mucho que', 'por más que'] }, 'Subjunctive'],
    examples: [
      {
        text: '[Por mucho que](por mucho que) [insistas](Subjunctive), no voy a cambiar de opinión.',
        english: 'No matter how much you insist, I’m not changing my mind.',
      },
      { text: '[Por más que](por más que) lo [intente](Subjunctive), no me sale.', english: 'However hard I try, I can’t get it right.' },
    ],
    note: 'With the indicative they describe real, repeated attempts: por más que lo intento, no me sale.',
  },
]
